import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import "./NavigationBar.css";
import { brand } from '../../config/brand';
import PictureImage from '../common/PictureImage';

/**
 * The main navigation bar for the website.
 * It includes links to different pages, a logo, and a hamburger menu for mobile view.
 * The navigation bar hides on scroll down and reappears on scroll up or when scrolling stops.
 */
const NavigationBar = () => {
  const location = useLocation();
  
  // State to manage the mobile menu's open/closed status
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  // State to manage the visibility of the navigation bar
  const [visible, setVisible] = useState(true);
  const [scrolled, setScrolled] = useState(false);
  
  // Refs for scroll tracking to prevent re-renders
  const prevScrollY = useRef(window.scrollY);
  const scrollTimeout = useRef(null);
  
  // Effect to handle scroll events for showing/hiding the navigation bar
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const scrollingUp = currentScrollY < prevScrollY.current;
      const nearTop = currentScrollY < 50;
      setScrolled(currentScrollY > 8);
      
      // Clear any existing timeout
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
        scrollTimeout.current = null;
      }
      
      // Show immediately when scrolling up or near top
      if (scrollingUp || nearTop) {
        setVisible(true);
      } 
      // Hide when scrolling down
      else {
        setVisible(false);
      }
      
      // Update previous scroll position
      prevScrollY.current = currentScrollY;
      
      // Set a timeout to show navbar after scrolling stops
      scrollTimeout.current = setTimeout(() => {
        setVisible(true);
        scrollTimeout.current = null;
      }, 300); // Set to 300ms for production
    };
    
    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimeout.current) {
        clearTimeout(scrollTimeout.current);
      }
    };
  }, []); 
  
  // Toggles the mobile menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };
  
  // Effect to prevent body scrolling when the mobile menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMenuOpen]);

  const isSubscriptionRoute = ['/login', '/pricing', '/billing'].includes(location.pathname);
  if (isSubscriptionRoute) return null;

  return (
    <nav className={`navigation-bar ${!visible ? 'hidden' : ''} ${scrolled ? 'scrolled' : ''}`}>
      <div className="hamburger-menu" onClick={toggleMenu}>
        <div className={`hamburger-bar ${isMenuOpen ? 'open' : ''}`}></div>
        <div className={`hamburger-bar ${isMenuOpen ? 'open' : ''}`}></div>
        <div className={`hamburger-bar ${isMenuOpen ? 'open' : ''}`}></div>
      </div>

      <div className={`nav-left ${isMenuOpen ? 'open' : ''}`}>
        {["HOME", "RETAILERS", "BRANDS", "RX", "ABOUT US", "BETA"].map((link, idx) => (
          <Link key={idx} to={`/${link.replace(/\s+/g, "-").toLowerCase()}`} className="nav-link" onClick={() => setIsMenuOpen(false)}>
            {link}
          </Link>
        ))}
        {/* {user ? (
          <button className="auth-button" onClick={handleLogout}>LOGOUT</button>
        ) : (
          <Link to="/login" className="nav-link" onClick={() => setIsMenuOpen(false)}>USER LOGIN</Link>
        )} */}
      </div>

      <div className="nav-right">
        <Link to="/">
          <PictureImage
            src="/home/rebrand/kaylus-blue-logo.webp"
            alt={brand.name}
            imgClassName="nav-logo"
          />
        </Link>
      </div>
    </nav>
  );
};

export default NavigationBar;
